import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CategoryProductErrorIcon, CategoryProductSuccessIcon } from '../../../../assets/icon';
import type { CategoryProductLink, MessageButton, PaymentAccountData, PaymentAccountItem, PaymentAccountStatus } from '../../../../types/chat-message';
import { paymentAccountWarning } from './paymentAccountUtils';
import { linkTextParts } from '../../../../utils';
import './PaymentAccountCard.scss';

interface Props {
  data: PaymentAccountData;
  canInteract: boolean;
  onConfirm?: (button: MessageButton) => void | Promise<void>;
  onLinkClick?: (url: string) => void;
}

const MarkdownText: React.FC<{ text: string; className: string }> = ({ text, className }) => (
  <div className={className}><ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown></div>
);

const BankLogo: React.FC<{ item: PaymentAccountItem }> = ({ item }) => {
  const [hasError, setHasError] = React.useState(false);
  React.useEffect(() => { setHasError(false); }, [item?.iconUrl]);
  if (!item?.iconUrl || hasError) return null;
  return <div className="payment-account-card__logo">
    <img src={item.iconUrl} alt={`Logo ${item?.bankName || ''}`}
      onError={() => setHasError(true)} />
  </div>;
};

const StatusIcon: React.FC<{ status?: PaymentAccountStatus | boolean }> = ({ status }) => {
  if (status === undefined || status === 'INVALID_INFO') return null;
  const isSuccess = status === 'SUCCESS' || status === true;
  const Icon = isSuccess ? CategoryProductSuccessIcon : CategoryProductErrorIcon;
  const label = isSuccess ? 'Tạo tài khoản thành công' : 'Tạo tài khoản thất bại';
  return <Icon className="payment-account-card__status" role="img" aria-label={label} />;
};

const itemState = (status?: PaymentAccountStatus | boolean): 'success' | 'error' | 'neutral' => {
  if (status === 'SUCCESS' || status === true) return 'success';
  if (status === 'FAIL' || status === false || status === 'INVALID_INFO') return 'error';
  return 'neutral';
};

const AccountDetails: React.FC<{ item: PaymentAccountItem }> = ({ item }) => {
  const warning = item ? paymentAccountWarning(item) : undefined;
  return <div className="payment-account-card__details"><strong>{item?.bankName}</strong>
    <span>{item?.accountNumber}</span><b>{item?.accountName?.toUpperCase()}</b>
    {warning && <em className="payment-account-card__warning">{warning}</em>}
  </div>;
};

const AccountItem: React.FC<{ item: PaymentAccountItem }> = ({ item }) => {
  const state = itemState(item?.status);
  const showLogo = item?.status !== 'INVALID_INFO';
  return <li className={`payment-account-card__item payment-account-card__item--${state}`}>
    <div className="payment-account-card__content">
      <div className="payment-account-card__main">
        {showLogo && <BankLogo item={item} />}
        <AccountDetails item={item} />
      </div>
      <StatusIcon status={item?.status} />
    </div>
  </li>;
};

const DetailLink: React.FC<{ link: CategoryProductLink; onClick?: (url: string) => void }> = ({ link, onClick }) => {
  const [before, after] = linkTextParts(link.text || link.url);
  return <div className="payment-account-card__link-text">
    {before && <MarkdownText text={before} className="payment-account-card__link-copy" />} {before && ' '}
    <button type="button" className="payment-account-card__link" disabled={!link.url} onClick={() => onClick?.(link.url)}>tại đây</button>
    {after && <MarkdownText text={after} className="payment-account-card__link-copy" />}
  </div>;
};

const buttonText = (button: MessageButton, count: number): string => (
  /\(\s*\d+\s*\)\s*$/.test(button.name) ? button.name : `${button.name}`
);

const PaymentAccountCard: React.FC<Props> = ({ data, canInteract, onConfirm, onLinkClick }) => (
  <section className="payment-account-card" aria-label="Danh sách tài khoản thanh toán">
    {data?.title && <MarkdownText text={data.title} className="payment-account-card__title" />}
    <ul className="payment-account-card__list">{data?.items?.map((item, index) => (
      <AccountItem key={item?.id || `${item?.bankCode || item?.bankName}-${item?.accountNumber}-${index}`} item={item} />
    ))}</ul>
    {!data?.isResult && canInteract && data?.buttons?.map((button, index) => (
      <button key={button?.id || index} type="button" className="payment-account-card__button"
        onClick={() => onConfirm?.(button)}>{buttonText(button, data.items?.length || 0)}</button>
    ))}
    {data?.links?.map((link, index) => (
      <DetailLink key={`${link?.url}-${index}`} link={link} onClick={onLinkClick} />
    ))}
  </section>
);

export default PaymentAccountCard;
